import { useState, useEffect } from 'react'
import { useNotify } from 'react-admin'
import { Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material'

import { CommonDialog } from '../../common/Dialog'
import { supabaseClient } from '../../supabase'

import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'

const PRESET_COLORS = [
	'#4db87a', '#5bbb6e', '#3b9ecc', '#4aaddc',
	'#9b6fd4', '#e8824a', '#e8621a', '#f5a623',
	'#d44f8e', '#2196f3',
]

const emptyPhase = () => ({
	_key: Math.random().toString(36).slice(2),
	label: '',
	start_date: '',
	end_date: '',
})

const ProjectTimelineDialog = ({ open, onClose, project, maxSortOrder }) => {
	const notify = useNotify()
	const isEdit = !!project

	const [name, setName] = useState('')
	const [description, setDescription] = useState('')
	const [color, setColor] = useState('#4db87a')
	const [phases, setPhases] = useState([emptyPhase()])
	const [saving, setSaving] = useState(false)

	useEffect(() => {
		if ( open ) {
			if ( project ) {
				setName(project.name)
				setDescription(project.description || '')
				setColor(project.color || '#4db87a')
				setPhases(
					(project.phases || []).map(ph => ({
						...ph,
						_key: ph.id || Math.random().toString(36).slice(2),
					}))
				)
			} else {
				setName('')
				setDescription('')
				setColor('#4db87a')
				setPhases([emptyPhase()])
			}
		}
	}, [open, project])

	const updatePhase = (index, field, value) => {
		setPhases(prev => prev.map((ph, i) => i === index ? { ...ph, [field]: value } : ph))
	}

	const addPhase = () => setPhases(prev => [...prev, emptyPhase()])

	const removePhase = (index) => {
		if ( phases.length <= 1 ) return
		setPhases(prev => prev.filter((_, i) => i !== index))
	}

	const handleSave = async () => {
		if ( !name.trim() ) {
			notify('Le nom du projet est requis', { type: 'warning' })
			return
		}

		const validPhases = phases.filter(ph => ph.label.trim() && ph.start_date && ph.end_date)
		if ( validPhases.length === 0 ) {
			notify('Au moins une phase complète est requise', { type: 'warning' })
			return
		}

		setSaving(true)

		try {
			let projectId

			if ( isEdit ) {
				const { error: updateError } = await supabaseClient
					.from('project_timeline')
					.update({ name: name.trim(), description: description.trim() || null, color })
					.eq('id', project.id)

				if ( updateError ) throw updateError
				projectId = project.id

				const { error: deleteError } = await supabaseClient
					.from('project_timeline_phases')
					.delete()
					.eq('project_id', project.id)

				if ( deleteError ) throw deleteError
			} else {
				const { data, error: insertError } = await supabaseClient
					.from('project_timeline')
					.insert({ name: name.trim(), description: description.trim() || null, color, sort_order: maxSortOrder + 1 })
					.select('id')
					.single()

				if ( insertError ) throw insertError
				projectId = data.id
			}

			const phasesToInsert = validPhases.map(ph => ({
				project_id: projectId,
				label: ph.label.trim(),
				start_date: ph.start_date,
				end_date: ph.end_date,
				color: color,
			}))

			const { error: phasesError } = await supabaseClient
				.from('project_timeline_phases')
				.insert(phasesToInsert)

			if ( phasesError ) throw phasesError

			notify(isEdit ? 'Projet mis à jour' : 'Projet créé', { type: 'success' })
			onClose()
		} catch (err) {
			console.error(err)
			notify(`Erreur: ${err.message}`, { type: 'error' })
		} finally {
			setSaving(false)
		}
	}

	return (
		<CommonDialog
			open={open}
			handleClose={onClose}
			title={isEdit ? `Modifier : ${project?.name}` : 'Nouveau projet'}
			size="md"
			displayDialogActions={false}
		>
			<Stack spacing={3}>
				<Stack direction="row" spacing={2} alignItems="flex-start">
					<TextField
						label="Nom du projet"
						value={name}
						onChange={e => setName(e.target.value)}
						fullWidth
						size="small"
						autoFocus
					/>

					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pt: 0.5 }}>
						{PRESET_COLORS.map(c => (
							<Box
								key={c}
								onClick={() => setColor(c)}
								sx={{
									width: 24,
									height: 24,
									borderRadius: '50%',
									backgroundColor: c,
									cursor: 'pointer',
									border: color === c ? '2px solid #333' : '2px solid transparent',
									'&:hover': { transform: 'scale(1.2)' },
									transition: 'transform 0.1s',
									flexShrink: 0,
								}}
							/>
						))}
					</Box>
				</Stack>

				<TextField
					label="Description"
					value={description}
					onChange={e => setDescription(e.target.value)}
					fullWidth
					size="small"
					multiline
					minRows={2}
				/>

				<Box>
					<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
						<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Phases</Typography>

						<Button size="small" startIcon={<AddIcon />} onClick={addPhase}>
							Ajouter une phase
						</Button>
					</Stack>

					<Stack spacing={1.5}>
						{phases.map((phase, index) => (
							<Stack
								key={phase._key}
								direction="row"
								spacing={1}
								alignItems="center"
								sx={{
									p: 1.5,
									borderRadius: 1,
									border: '1px solid',
									borderColor: 'divider',
								}}
							>
								<TextField
									label="Label"
									value={phase.label}
									onChange={e => updatePhase(index, 'label', e.target.value)}
									size="small"
									sx={{ flex: 1 }}
								/>

								<TextField
									label="Début"
									type="date"
									value={phase.start_date}
									onChange={e => updatePhase(index, 'start_date', e.target.value)}
									size="small"
									InputLabelProps={{ shrink: true }}
									sx={{ width: 160 }}
								/>

								<TextField
									label="Fin"
									type="date"
									value={phase.end_date}
									onChange={e => updatePhase(index, 'end_date', e.target.value)}
									size="small"
									InputLabelProps={{ shrink: true }}
									sx={{ width: 160 }}
								/>

								<IconButton
									size="small"
									onClick={() => removePhase(index)}
									disabled={phases.length <= 1}
									sx={{ color: '#f44336' }}
								>
									<DeleteIcon fontSize="small" />
								</IconButton>
							</Stack>
						))}
					</Stack>
				</Box>

				<Stack direction="row" spacing={1} justifyContent="flex-end">
					<Button onClick={onClose} disabled={saving}>
						Annuler
					</Button>

					<Button variant="contained" onClick={handleSave} disabled={saving}>
						{saving ? 'Enregistrement...' : isEdit ? 'Mettre à jour' : 'Créer'}
					</Button>
				</Stack>
			</Stack>
		</CommonDialog>
	)
}

export default ProjectTimelineDialog
